import { useState } from 'react'
import { useSnackbar } from 'notistack'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import * as Yup from 'yup'

import { yupResolver } from '@hookform/resolvers/yup'
import { MenuItem } from '@mui/material'

import { useMultiPartFileUpload } from '~/features/file-upload/queries/useFileUpload'
import {
  useInquiryCategory,
  useInquiryCreate,
} from '~/features/inquiry/queries/useInquiry'
import InquiryBgMobile from '~/shared/assets/background/inquiry-bg-mobile.png'
import InquiryBgPc from '~/shared/assets/background/inquiry-bg-pc.png'
import Back from '~/shared/assets/icons/Back'
import ImageUpload from '~/shared/assets/icons/ImageUpload'
import RHFCheckBox from '~/shared/components/hook-form/rhf-checkbox'
import RHFSelect from '~/shared/components/hook-form/rhf-select'
import RHFTextArea from '~/shared/components/hook-form/rhf-text-area'
import RHFTextField from '~/shared/components/hook-form/rhf-text-field'
import LoadingScreen from '~/shared/components/loading/LoadingScreen'
import useViewport from '~/shared/hooks/useViewport'
import FormProvider from '~/shared/providers/FormProvider'
import theme from '~/shared/styles/theme'

function InquiryCreatePage() {
  const viewport = useViewport()
  const navigate = useNavigate()
  const location = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const isMobile = viewport < theme.deviceSizes.medium
  const backgroundImage = isMobile ? InquiryBgMobile : InquiryBgPc

  const { categoryValue } = location.state || {}

  const [thumbnail, setThumbnail] = useState({
    id: 0,
    url: '',
  })

  const { data: categoryList, isLoading } = useInquiryCategory()
  const { mutate: fileUploadMutate } = useMultiPartFileUpload()
  const { mutate: postInquiry } = useInquiryCreate()

  const newInquirySchema = Yup.object().shape({
    title: Yup.string().required('제목을 입력해주세요.'),
    category: Yup.string().required('문의 유형을 선택해주세요.'),
    body: Yup.string().required('본문을 입력해주세요.'),
  })

  const defaultValues = {
    title: '',
    category: categoryValue
      ? categoryValue
      : categoryList && categoryList.length > 0
        ? categoryList[0].value
        : '',
    body: '',
  }

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(newInquirySchema),
    defaultValues,
  })

  const {
    handleSubmit,
    formState: { errors },
  } = methods

  const onSubmit = handleSubmit(async (data) => {
    if (data) {
      const newData = {
        ...data,
        image: thumbnail.id,
      }
      postInquiry(newData, {
        onSuccess: () => {
          navigate('/customer-service/inquiry')
        },
      })
    }
  })

  const handleClickBack = () => {
    navigate('/customer-service/inquiry')
  }

  const checkFileSize = (file: File, maxSizeMB: number): boolean => {
    const MAX_FILE_SIZE = maxSizeMB * 1024 * 1024
    if (file.size > MAX_FILE_SIZE) {
      enqueueSnackbar(`파일 크기는 ${maxSizeMB}MB를 초과할 수 없습니다.`, {
        variant: 'error',
      })

      return false
    }

    return true
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()

    if (e.target.files) {
      const file = e.target.files[0]

      if (!checkFileSize(file, 5)) return

      fileUploadMutate(file, {
        onSuccess: (data) => {
          setThumbnail(data.data.data.file_list[0])
        },
        onError: (error) => {
          enqueueSnackbar(error.message, { variant: 'error' })
        },
      })
    }
  }

  if (isLoading) return <LoadingScreen />

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Wrapper>
        <Title>문의하기</Title>
        <BackgroundImage src={backgroundImage} alt="background image" />
        <Container>
          <BackBox onClick={handleClickBack}>
            <Back />
            <span className="text">돌아가기</span>
          </BackBox>
          <TitleContainer>
            <Label>제목</Label>
            <TitleTextField name="title" placeholder="제목을 입력해주세요" />
          </TitleContainer>
          {errors.title && <ErrorText>{errors.title.message}</ErrorText>}
          <CategoryContainer>
            <Label>문의 유형 선택</Label>
            {isMobile ? (
              <RHFSelect name="category">
                {categoryList?.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </RHFSelect>
            ) : (
              <CategoryBoxContainer>
                {categoryList?.map((category) => (
                  <RHFCheckBox
                    key={category.value}
                    name="category"
                    options={category}
                  />
                ))}
              </CategoryBoxContainer>
            )}

            {errors.category && (
              <ErrorText>{errors.category?.message as string}</ErrorText>
            )}
          </CategoryContainer>
          <BodyContainer>
            <Label>본문 작성</Label>
            <BodyTextArea name="body" placeholder="문의할 내용을 남겨주세요." />
          </BodyContainer>
          {errors.body && <ErrorText>{errors.body.message}</ErrorText>}
          <ImageContainer>
            <Label>첨부 이미지</Label>
            <ImageBoxWrapper>
              <FileLabel htmlFor="file">
                <ImageUpload />
                이미지 등록
                <FileInput
                  type="file"
                  name="image"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </FileLabel>
              <ImageGuideText>*jpg/png의 5MB 이하 이미지</ImageGuideText>
            </ImageBoxWrapper>
            {thumbnail && thumbnail.url && (
              <Thumbnail src={thumbnail.url} alt="thumbnail" />
            )}
          </ImageContainer>
          <ButtonContainer>
            <CreateButton type="submit">문의 등록하기</CreateButton>
            <CancelButton onClick={handleClickBack}>취소하기</CancelButton>
          </ButtonContainer>
        </Container>
      </Wrapper>
    </FormProvider>
  )
}

export default InquiryCreatePage

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: center;

  box-sizing: border-box;
  width: 100vw;
  max-width: 1080px;
  min-height: calc(100vh - ${theme.size.headerHeight}px);
  margin: 0 auto;
  margin-top: calc(80px + ${theme.size.headerHeight}px);
  margin-bottom: 120px;

  @media ${theme.device.medium} {
    gap: 24px;
    align-items: start;

    width: 90vw;
    height: auto;
    margin-top: calc(56px + ${theme.size.headerHeight}px);
  }
`

const Title = styled.h2`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 42px;
  font-weight: 700;
  line-height: 58px;
  color: #313634;

  @media ${theme.device.medium} {
    font-size: 28px;
    line-height: 39px;
  }
`

const BackgroundImage = styled.img`
  position: absolute;
  z-index: ${theme.zIndex.belowBackground};
  top: 0;
  left: 0;

  overflow: hidden;

  width: 100%;
  height: 100%;

  background-repeat: no-repeat;

  @media ${theme.device.medium} {
    /* height: 100%; */
    background-repeat: repeat;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: start;

  box-sizing: border-box;
  width: 100%;
  padding: 36px 24px;

  background-color: white;
  border-radius: 12px;

  @media ${theme.device.medium} {
    gap: 24px;
    padding: 20px 24px;
  }
`

const BackBox = styled.div`
  cursor: pointer;
  display: flex;
  gap: 12px;
  align-items: center;

  .text {
    font-family: NanumSquareNeo-Regular, sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 21px;
    color: ${theme.colors.grey_4};
  }
`

const TitleContainer = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;

  width: 100%;
  height: 49px;

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 10px;
    align-items: start;
    height: 100%;
  }
`

const Label = styled.span`
  width: 130px;
  font-size: 15px;
  line-height: 21px;
  color: #838685;
`

const TitleTextField = styled(RHFTextField)`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 20px 12px;

  font-size: 15px;
  line-height: 21px;
  color: ${theme.colors.grey_4};

  background-color: #f0f0f0;
  border: none;
  border-bottom: 1px solid ${theme.colors.grey_2};
  border-radius: 12px;
  outline: none;

  &::placeholder {
    font-family: NanumSquareNeo-Regular, sans-serif;
    font-size: 18px;
    font-weight: 500;
    line-height: 25px;
    color: #838685;

    @media ${theme.device.medium} {
      font-size: 15px;
      line-height: 21px;
    }
  }

  @media ${theme.device.medium} {
    height: 45px;
  }
`

const CategoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media ${theme.device.medium} {
    width: 100%;
  }
`

const CategoryBoxContainer = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
    width: 100%;
  }
`

const ErrorText = styled.span`
  margin-top: 4px;
  font-size: 12px;
  color: ${theme.colors.warning};
`

const BodyContainer = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
  }
`

const BodyTextArea = styled(RHFTextArea)`
  box-sizing: border-box;
  width: 100%;
  height: 292px;
  padding: 20px 12px;

  background-color: #f0f0f0;
  border: none;
  border-radius: 12px;

  &::placeholder {
    font-family: NanumSquareNeo-Regular, sans-serif;
    font-size: 18px;
    font-weight: 500;
    line-height: 25px;
    color: #838685;
  }

  @media ${theme.device.medium} {
    height: 292px;
  }
`

const ImageContainer = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
  }
`

const ImageBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const ImageGuideText = styled.p`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 21px;
  color: #838685;
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: center;

  width: 100%;

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
  }
`

const CreateButton = styled.button`
  cursor: pointer;

  box-sizing: border-box;
  width: fit-content;
  min-width: 200px;
  height: 58px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 24px;
  font-weight: 500;
  line-height: 33px;
  color: white;

  background-color: #313634;
  border-radius: 54px;

  @media ${theme.device.medium} {
    width: 100%;
  }
`

const CancelButton = styled.button`
  cursor: pointer;

  box-sizing: border-box;
  width: fit-content;
  min-width: 200px;
  height: 58px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 24px;
  font-weight: 500;
  line-height: 33px;
  color: #313634;

  background-color: white;
  border-radius: 54px;

  @media ${theme.device.medium} {
    width: 100%;
    border: 2px solid #313634;
  }
`
const FileLabel = styled.label`
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;

  width: 132px;
  height: 132px;

  font-weight: 500;

  background: ${theme.colors.grey_1};
  border-radius: 10px;
`

const FileInput = styled.input`
  cursor: pointer;

  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  opacity: 0;
  background-color: red;
`

const Thumbnail = styled.img`
  width: 132px;
  height: 132px;
  object-fit: cover;
  border-radius: 10px;
`
